import { z } from 'zod';
import { isoDateTime, wireDecimal, type DecimalString } from './common';
import { wireOrderSchema, type Order } from './orders';
import { normalizeOrder } from './orders';

/**
 * Order editing wire contract (V2.3-01) — PUT {pos}/orders/{id}.
 *
 * Verified against:
 * - pos-api/app/Http/Controllers/Api/POS/OrderController.php::update
 * - salesgroundai/app/Http/Controllers/API/Internal/OrderController.php::update
 * - salesgroundai/app/Services/OrderEditService.php
 * - salesgroundai/app/Models/OrderEdit.php::toResponsePayload
 *
 * THE PAYLOAD IS A DESIRED STATE, NOT A PATCH. `items` is the complete set the
 * order should end up with; every other key is applied only when PRESENT. That
 * is why the builder below omits keys rather than sending nulls — a forwarded
 * null reads upstream as "clear this field" and would blank a delivery address
 * the editor never displayed.
 *
 * Unit prices and totals are computed by the write service. Changing a manual
 * discount needs `orders.discount`; an explicit ecommerce shipping fee is
 * validated and included in the authoritative preview before saving.
 */

// ---------------------------------------------------------------------------
// request
// ---------------------------------------------------------------------------

export interface OrderEditItemPayload {
  product_id: number;
  quantity: number;
  /** Persisted verbatim; item notes ride as `attributes.notes`. */
  attributes: Record<string, string>;
  /** STRING index of the parent item within this same items array. */
  parent_key?: string;
}

export interface OrderEditPayload {
  quote_fingerprint?: string;
  items: OrderEditItemPayload[];
  notes?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  shipping_address?: {
    street: string;
    name?: string;
    phone?: string;
    address_line_2?: string;
    city?: string;
    state?: string;
    country?: string;
    country_code?: string;
    postcode?: string;
  };
  area_id?: number | null;
  table_id?: number | null;
  /** Dine-in head count. Editable since V2.3-02; refused on other types. */
  covers?: number | null;
  /** Rupees. Server-repriced and gated on `orders.discount`. */
  discount_amount?: number;
  /** Ecommerce delivery only: final shipping fee after shipping discounts, in rupees. */
  shipping_charge?: number;
  /** Required upstream for a delivered/settled/cancelled/refunded order. */
  reason?: string;
  /** The revision the editor LOADED. A stale one is a 409, never an overwrite. */
  expected_revision: number;
  /** Operation key: a retry after a lost response returns the original edit. */
  client_edit_id: string;
}

// ---------------------------------------------------------------------------
// response
// ---------------------------------------------------------------------------

const wireEditTotalsSideSchema = z.looseObject({
  sub_total: wireDecimal,
  delivery_charges: wireDecimal,
  discount_amount: wireDecimal,
  total: wireDecimal,
});

/**
 * One line as the KITCHEN is told about it.
 *
 * No price field exists on this shape, at either end. The correction ticket is
 * rendered from exactly these entries, so a money field here would be a money
 * field on a page left open at the pass.
 */
export const wireKitchenLineSchema = z.looseObject({
  product_id: z.number(),
  name: z.string().nullish(),
  quantity: z.number(),
  attributes: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]).nullish(),
  is_addon: z.boolean().nullish(),
});

export const wireOrderEditSchema = z.looseObject({
  id: z.number(),
  revision: z.number(),
  edited_by_user_id: z.number().nullish(),
  reason: z.string().nullish(),
  created_at: isoDateTime.nullish(),
  order_status: z.string().nullish(),
  totals: z.looseObject({
    old: wireEditTotalsSideSchema,
    new: wireEditTotalsSideSchema,
    /** Signed: positive means the order now costs more than it did. */
    delta: wireDecimal,
  }),
  payment: z.looseObject({
    /** Null when nothing ever recorded a payment against this order. */
    paid_amount: wireDecimal.nullish(),
    balance_due: wireDecimal.nullish(),
    refund_due: wireDecimal.nullish(),
    /** Always false. There is no processor; both figures are for a human. */
    settlement_captured: z.boolean().nullish(),
  }),
  item_delta: z
    .looseObject({
      added: z.array(z.looseObject({})).default([]),
      removed: z.array(z.looseObject({})).default([]),
      changed: z.array(z.looseObject({})).default([]),
    })
    .nullish(),
  kitchen_delta: z
    .looseObject({
      additions: z.array(wireKitchenLineSchema).default([]),
      voids: z.array(wireKitchenLineSchema).default([]),
      /** Where the guests moved to, when they moved. Null on every other edit. */
      table_move: z
        .looseObject({
          from_table_id: z.number().nullish(),
          from_label: z.string().nullish(),
          to_table_id: z.number().nullish(),
          to_label: z.string().nullish(),
        })
        .nullish(),
      /** Whether the pass needs to be told NOW (fired, and not a final record). */
      requires_kot: z.boolean().default(false),
      /** A move is a separate notice: the food is unchanged, the seat is not. */
      requires_table_notice: z.boolean().default(false),
    })
    .nullish(),
  replayed: z.boolean().nullish(),
});
export type WireOrderEdit = z.infer<typeof wireOrderEditSchema>;

export const wireOrderEditResponseSchema = z.looseObject({
  success: z.literal(true),
  message: z.string().nullish(),
  order: wireOrderSchema,
  edit: wireOrderEditSchema,
  replayed: z.boolean().nullish(),
});

/** 409 body when another device saved first. Carries the FRESH order. */
export const wireOrderEditConflictSchema = z.looseObject({
  code: z.string().nullish(),
  error: z.string().nullish(),
  order: wireOrderSchema.nullish(),
  current_revision: z.number().nullish(),
});

// ---------------------------------------------------------------------------
// normalized
// ---------------------------------------------------------------------------

export interface EditTotalsSide {
  subTotal: DecimalString;
  deliveryCharges: DecimalString;
  discountAmount: DecimalString;
  total: DecimalString;
}

export interface KitchenLine {
  productId: number;
  name: string;
  quantity: number;
  /** Option metadata, minus `notes`. */
  options: Array<[string, string]>;
  note: string | null;
  isAddon: boolean;
}

/** A tab that changed seat. Labels are what a person carrying a plate reads. */
export interface TableMove {
  fromTableId: number | null;
  fromLabel: string | null;
  toTableId: number | null;
  toLabel: string | null;
}

export interface KitchenDelta {
  additions: KitchenLine[];
  voids: KitchenLine[];
  /** Null on every edit that did not move the tab. */
  tableMove: TableMove | null;
  /** True when the pass is actively cooking this order and owes a correction. */
  requiresKot: boolean;
  /**
   * True when the tab moved and the kitchen already has the order.
   *
   * SEPARATE from `requiresKot` because the food is unchanged: the runner
   * carrying it simply has the wrong table number in their head. Folding a
   * seating change into the ADD list is how a station cooks it a second time.
   */
  requiresTableNotice: boolean;
}

export interface OrderEditResult {
  id: number;
  revision: number;
  reason: string | null;
  createdAt: string | null;
  orderStatus: string | null;
  oldTotals: EditTotalsSide;
  newTotals: EditTotalsSide;
  /** Signed decimal string: positive means the order now costs more. */
  totalDelta: DecimalString;
  /** What was recorded as collected, or null when nothing ever was. */
  paidAmount: DecimalString | null;
  balanceDue: DecimalString | null;
  refundDue: DecimalString | null;
  kitchen: KitchenDelta;
  itemsAdded: number;
  itemsRemoved: number;
  itemsChanged: number;
  /** True when this response replayed an edit that had already been applied. */
  replayed: boolean;
}

export interface AppliedOrderEdit {
  order: Order;
  edit: OrderEditResult;
}

function normalizeTotalsSide(wire: z.infer<typeof wireEditTotalsSideSchema>): EditTotalsSide {
  return {
    subTotal: wire.sub_total,
    deliveryCharges: wire.delivery_charges,
    discountAmount: wire.discount_amount,
    total: wire.total,
  };
}

function stringEntries(attributes: unknown): Array<[string, string]> {
  if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) return [];
  return Object.entries(attributes as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  );
}

function normalizeKitchenLine(wire: z.infer<typeof wireKitchenLineSchema>): KitchenLine {
  const entries = stringEntries(wire.attributes);
  return {
    productId: wire.product_id,
    name: wire.name ?? `Product #${wire.product_id}`,
    quantity: wire.quantity,
    options: entries.filter(([key]) => key !== 'notes'),
    note: entries.find(([key]) => key === 'notes')?.[1] ?? null,
    isAddon: wire.is_addon === true,
  };
}

export function normalizeOrderEdit(wire: WireOrderEdit, replayed = false): OrderEditResult {
  const kitchen = wire.kitchen_delta;
  const items = wire.item_delta;
  return {
    id: wire.id,
    revision: wire.revision,
    reason: wire.reason ?? null,
    createdAt: wire.created_at ?? null,
    orderStatus: wire.order_status ?? null,
    oldTotals: normalizeTotalsSide(wire.totals.old),
    newTotals: normalizeTotalsSide(wire.totals.new),
    totalDelta: wire.totals.delta,
    paidAmount: wire.payment.paid_amount ?? null,
    balanceDue: wire.payment.balance_due ?? null,
    refundDue: wire.payment.refund_due ?? null,
    kitchen: {
      additions: (kitchen?.additions ?? []).map(normalizeKitchenLine),
      voids: (kitchen?.voids ?? []).map(normalizeKitchenLine),
      tableMove: kitchen?.table_move
        ? {
            fromTableId: kitchen.table_move.from_table_id ?? null,
            fromLabel: kitchen.table_move.from_label ?? null,
            toTableId: kitchen.table_move.to_table_id ?? null,
            toLabel: kitchen.table_move.to_label ?? null,
          }
        : null,
      requiresKot: kitchen?.requires_kot === true,
      requiresTableNotice: kitchen?.requires_table_notice === true,
    },
    itemsAdded: items?.added.length ?? 0,
    itemsRemoved: items?.removed.length ?? 0,
    itemsChanged: items?.changed.length ?? 0,
    replayed: replayed || wire.replayed === true,
  };
}

export function normalizeAppliedEdit(
  body: z.infer<typeof wireOrderEditResponseSchema>,
): AppliedOrderEdit {
  return {
    order: normalizeOrder(body.order),
    edit: normalizeOrderEdit(body.edit, body.replayed === true),
  };
}
